import { format, parse } from 'date-fns'
import type { RevenueTransaction } from '@/lib/utils/revenueUtils'
import { toCents } from '@/lib/utils/revenueUtils'

/**
 * Pure helpers behind the Attribution page states (Paper "03 - App ·
 * Attribution"): which state to show, the "Where paying customers came from"
 * rows, and the copy that reads data back as sentences. No React, so the
 * state rules are unit-tested (`src/__tests__/components/attribution/`).
 */

export type AttributionView =
  | 'loading'
  | 'nothing-connected'
  | 'ads-no-stripe'
  | 'no-payments'
  | 'stripe-only'
  | 'full'

export function selectAttributionView({
  settled,
  hasAds,
  revenueConfigured,
  paymentCount,
  revenueDollars,
}: {
  settled: boolean
  /** An ad account is connected or the range has ad spend */
  hasAds: boolean
  /** Stripe or Foxy is configured */
  revenueConfigured: boolean
  paymentCount: number
  revenueDollars: number
}): AttributionView {
  if (!settled) return 'loading'
  if (!revenueConfigured) {
    // Revenue already flowing (legacy tracking) still reads as a report
    if (revenueDollars > 0) return hasAds ? 'full' : 'stripe-only'
    return hasAds ? 'ads-no-stripe' : 'nothing-connected'
  }
  if (paymentCount === 0 && revenueDollars <= 0) return 'no-payments'
  return hasAds ? 'full' : 'stripe-only'
}

// ---------------------------------------------------------------------------
// Money and counts
// ---------------------------------------------------------------------------

/** Whole dollars with separators ($4,370); cents under $10; M above a million. */
export function fmtMoney(value: number): string {
  if (!Number.isFinite(value) || value === 0) return '$0'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (abs >= 10) return `${sign}$${Math.round(abs).toLocaleString('en-US')}`
  return `${sign}$${abs.toFixed(2)}`
}

/** 126K / 2,410: compact above 100K so a funnel step stays one short number. */
export function fmtCount(value: number): string {
  if (!Number.isFinite(value)) return '0'
  if (Math.abs(value) >= 100_000) return `${Math.round(value / 1000).toLocaleString('en-US')}K`
  return Math.round(value).toLocaleString('en-US')
}

/** Step-to-step rate; null when the populations don't nest (a rate over 100%). */
export function stepRate(next: number, prev: number): string | null {
  if (prev <= 0 || next < 0) return null
  const r = next / prev
  if (r > 1) return null
  const pct = r * 100
  return pct < 10 ? `${pct.toFixed(1)}%` : `${Math.round(pct)}%`
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const ymd = (s: string) => parse(s, 'yyyy-MM-dd', new Date())

/** "Sep 19 to Sep 25" from local date-only bounds. */
export function rangeLabel(range: { since: string; until: string } | null | undefined): string | null {
  if (!range) return null
  const a = format(ymd(range.since), 'MMM d')
  const b = format(ymd(range.until), 'MMM d')
  return a === b ? a : `${a} to ${b}`
}

export function rangeDays(range: { since: string; until: string } | null | undefined): number {
  if (!range) return 0
  const ms = ymd(range.until).getTime() - ymd(range.since).getTime()
  return Math.max(1, Math.round(ms / 86_400_000) + 1)
}

export function todayYmd(now = new Date()): string {
  return format(now, 'yyyy-MM-dd')
}

const PERIOD_PHRASES: Record<string, string> = {
  '1d': 'the last 24 hours',
  PAST_24_H: 'the last 24 hours',
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  '7d': 'the last 7 days',
  LAST_7_DAYS: 'the last 7 days',
  '30d': 'the last 30 days',
  LAST_30_DAYS: 'the last 30 days',
  '90d': 'the last 90 days',
  LAST_90_DAYS: 'the last 90 days',
  mtd: 'this month',
  MONTH_TO_DATE: 'this month',
  ytd: 'this year',
  YEAR_TO_DATE: 'this year',
  '12m': 'the last 12 months',
  LAST_12_MONTHS: 'the last 12 months',
}

/** "the last 24 hours"; custom ranges read as "this range". */
export function periodPhrase(period: string | undefined): string {
  return (period && PERIOD_PHRASES[period]) || 'this range'
}

// ---------------------------------------------------------------------------
// Campaign copy
// ---------------------------------------------------------------------------

const FRACTIONS: Array<[number, string]> = [
  [1 / 4, 'A quarter of'],
  [1 / 3, 'A third of'],
  [1 / 2, 'Half'],
  [2 / 3, 'Two thirds of'],
  [3 / 4, 'Three quarters of'],
]

/** "Half" / "A third of" when the share sits within 4 points of one, else "41% of". */
export function shareWords(share: number): string {
  for (const [f, words] of FRACTIONS) {
    if (Math.abs(share - f) <= 0.04) return words
  }
  return `${Math.round(share * 100)}% of`
}

/** acme.com/agencies from a full URL or a bare path. */
export function landingLabel(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const u = new URL(url)
    const host = u.hostname.replace(/^www\./, '')
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '')
    return `${host}${path}`
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '') || null
  }
}

/**
 * "Half its ad clicks leave before acme.com/agencies finishes loading": only
 * when fewer than `threshold` of a campaign's clicks became visits.
 */
export function clickDropCopy(
  clickToSessionRate: number | null | undefined,
  landing: string | null | undefined,
  threshold = 0.6,
): string | null {
  if (clickToSessionRate == null || !Number.isFinite(clickToSessionRate)) return null
  if (clickToSessionRate >= threshold) return null
  const drop = Math.min(1, Math.max(0, 1 - clickToSessionRate))
  const page = landingLabel(landing) ?? 'the page'
  return `${shareWords(drop)} its ad clicks leave before ${page} finishes loading`
}

// ---------------------------------------------------------------------------
// Sources: where paying customers came from
// ---------------------------------------------------------------------------

type SourceKind = 'utm' | 'referrer' | 'direct'

export type SourceRevenueRow = {
  key: string
  name: string
  description: string | null
  /** Unique visitors from this source in range; null when traffic has no matching row */
  visitors: number | null
  payments: number
  earnedDollars: number
}

const KNOWN_REFERRERS: Array<{ test: RegExp; name: string; description: string }> = [
  { test: /(^|\.)google\./, name: 'Google search', description: 'Search results, no ad clicked' },
  { test: /(^|\.)bing\.com$/, name: 'Bing search', description: 'Search results, no ad clicked' },
  { test: /(^|\.)duckduckgo\.com$/, name: 'DuckDuckGo', description: 'Search results, no ad clicked' },
  { test: /(^|\.)(chatgpt\.com|chat\.openai\.com|openai\.com)$/, name: 'ChatGPT', description: 'Linked from an AI answer' },
  { test: /(^|\.)perplexity\.ai$/, name: 'Perplexity', description: 'Linked from an AI answer' },
  { test: /(^|\.)claude\.ai$/, name: 'Claude', description: 'Linked from an AI answer' },
  { test: /(^|\.)gemini\.google\.com$/, name: 'Gemini', description: 'Linked from an AI answer' },
  { test: /(^|\.)(linkedin\.com|lnkd\.in)$/, name: 'LinkedIn', description: 'Posts and profile links, no ad' },
  { test: /(^|\.)(x\.com|twitter\.com|t\.co)$/, name: 'X', description: 'Posts and profile links, no ad' },
  { test: /(^|\.)(facebook\.com|fb\.com|l\.facebook\.com)$/, name: 'Facebook', description: 'Posts and profile links, no ad' },
  { test: /(^|\.)instagram\.com$/, name: 'Instagram', description: 'Posts and profile links, no ad' },
  { test: /(^|\.)reddit\.com$/, name: 'Reddit', description: 'Posts and comments, no ad' },
  { test: /(^|\.)(youtube\.com|youtu\.be)$/, name: 'YouTube', description: 'Video pages and descriptions' },
]

const norm = (v: unknown) => String(v ?? '').toLowerCase().trim()
const host = (v: string) => v.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]

/** Which source a visit or payment belongs to: utm_source first, then referrer, else direct. */
export function sourceKeyOf(row: { utm_source?: unknown; referer_group?: unknown }): { key: string; kind: SourceKind } {
  const utm = norm(row.utm_source)
  if (utm && utm !== 'unknown') return { key: `utm:${utm}`, kind: 'utm' }
  const ref = norm(row.referer_group)
  if (!ref || ref === 'direct' || ref === '(direct)' || ref === 'none') return { key: 'direct', kind: 'direct' }
  return { key: `ref:${host(ref)}`, kind: 'referrer' }
}

export function describeSource(key: string): { name: string; description: string | null } {
  if (key === 'direct') return { name: 'Direct', description: 'Typed the address or used a bookmark' }
  if (key.startsWith('utm:')) {
    const v = key.slice(4)
    return { name: v, description: `Tagged utm_source=${v}` }
  }
  const domain = key.replace(/^ref:/, '')
  const known = KNOWN_REFERRERS.find((r) => r.test.test(domain))
  if (known) return { name: known.name, description: known.description }
  return { name: domain, description: `Linked from ${domain}` }
}

/**
 * Group matched payments by the source of the visit they matched, joined to
 * that source's visitors. Payments without session context are left out:
 * they have no source (the headline still counts them in the total).
 */
export function buildSourceRevenueRows({
  payments,
  utmSources,
  referrers,
}: {
  payments: RevenueTransaction[]
  /** utm_source → visitors */
  utmSources: Array<{ source: string; sessions: number }>
  /** referrer rows (no utm_medium): referer_group → visitors; null referrer is direct */
  referrers: Array<{ referer_group: string | null; count: number }>
}): SourceRevenueRow[] {
  const visitors = new Map<string, number>()
  for (const s of utmSources) {
    const { key } = sourceKeyOf({ utm_source: s.source })
    visitors.set(key, (visitors.get(key) ?? 0) + (Number(s.sessions) || 0))
  }
  for (const r of referrers) {
    const { key } = sourceKeyOf({ referer_group: r.referer_group })
    visitors.set(key, (visitors.get(key) ?? 0) + (Number(r.count) || 0))
  }

  const groups = new Map<string, { payments: number; cents: number }>()
  for (const tx of payments) {
    if (!tx.session_id && !tx.view_id && !tx.utm_source && !tx.referer_group) continue
    const cents = toCents(tx)
    if (!cents) continue
    const { key } = sourceKeyOf(tx)
    const g = groups.get(key) ?? { payments: 0, cents: 0 }
    g.payments += 1
    g.cents += cents
    groups.set(key, g)
  }

  return Array.from(groups.entries())
    .map(([key, g]) => ({
      key,
      ...describeSource(key),
      visitors: visitors.has(key) ? visitors.get(key)! : null,
      payments: g.payments,
      earnedDollars: g.cents / 100,
    }))
    .sort((a, b) => b.earnedDollars - a.earnedDollars || b.payments - a.payments)
}

/** The source most trials came from, for "14 from Google search". */
export function topTrialSource(
  trials: Array<{ utm_source?: unknown; referer_group?: unknown; session_id?: unknown; view_id?: unknown }>,
): { name: string; count: number } | null {
  const counts = new Map<string, number>()
  for (const t of trials) {
    if (!t.session_id && !t.view_id && !t.utm_source && !t.referer_group) continue
    const { key } = sourceKeyOf(t)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  let best: [string, number] | null = null
  for (const entry of counts) if (!best || entry[1] > best[1]) best = entry
  return best ? { name: describeSource(best[0]).name, count: best[1] } : null
}

/**
 * Distinct paying customers when payments carry a customer id; otherwise null
 * and the page counts payments instead of claiming people.
 */
export function distinctCustomers(payments: RevenueTransaction[]): number | null {
  const ids = new Set<string>()
  for (const p of payments) {
    const id = p.customer_id ?? p.stripe_customer_id ?? p.customer
    if (id == null || id === '') return null
    ids.add(String(id))
  }
  return payments.length > 0 ? ids.size : null
}

/** "Meta" / "Google" when one platform carries the spend, else null (several). */
export function adPlatformName(meta: number, google: number): 'Meta' | 'Google' | null {
  if (meta > 0 && google === 0) return 'Meta'
  if (google > 0 && meta === 0) return 'Google'
  return null
}

// ---------------------------------------------------------------------------
// Headlines: the answer first, in one sentence
// ---------------------------------------------------------------------------

type Money = (dollars: number) => string
const plural = (n: number, one: string, many = `${one}s`) => `${fmtCount(n)} ${n === 1 ? one : many}`

/** "Google search brought in $1,820 of the $4,370 you collected." */
export function stripeOnlyHeadline(rows: SourceRevenueRow[], totalDollars: number, money: Money): string {
  const top = rows[0]
  if (top && top.earnedDollars > 0) {
    return `${top.name} brought in ${money(top.earnedDollars)} of the ${money(totalDollars)} you collected.`
  }
  return `${money(totalDollars)} collected. None of it matched a visit yet.`
}

/** "19 payments in Stripe, Sep 19 to Sep 25" */
export function stripeOnlyMeta(paymentCount: number, provider: string, range: string | null): string {
  return [`${plural(paymentCount, 'payment')} in ${provider}`, range].filter(Boolean).join(', ')
}

/** "Meta ads brought 1,890 visits for $3,120. What they earned needs Stripe." */
export function adsNoRevenueHeadline(platform: string | null, landed: number, spend: number, provider: string, money: Money): string {
  return `${platform ? `${platform} ads` : 'Ads'} brought ${plural(landed, 'visit')} for ${money(spend)}. What they earned needs ${provider}.`
}

/** "Sep 19 to Sep 25 · $1.29 a click · $1.65 per visit that loaded" */
export function adsNoRevenueMeta(range: string | null, spend: number, clicks: number, landed: number, money: Money): string {
  return [
    range,
    clicks > 0 && spend > 0 ? `${money(spend / clicks)} a click` : null,
    landed > 0 && spend > 0 ? `${money(spend / landed)} per visit that loaded` : null,
  ].filter(Boolean).join(' · ')
}

/** "Meta ads returned $1.40 for every $1. 19 paying customers, $164 ad spend per customer." */
export function adsHeadline(
  platform: string | null,
  earned: number,
  spend: number,
  paid: number,
  money: Money,
): string {
  const who = platform ? `${platform} ads` : 'Ads'
  // Only payments joined to an ad campaign count here. Revenue from organic,
  // direct or referral visits is never ad return, however it lines up by day.
  if (!(earned > 0)) {
    return spend > 0
      ? `${who} cost ${money(spend)}. No payment traced back to an ad click yet.`
      : 'No ad revenue matched yet in this range.'
  }
  if (!(spend > 0)) return `${who} brought in ${money(earned)} with no spend recorded in this range.`
  const ret = `${who} returned ${money(earned / spend)} for every $1.`
  // `paid` counts customers: a renewal is another payment from the same one.
  return paid > 0 ? `${ret} ${plural(paid, 'paying customer')}, ${money(spend / paid)} ad spend per customer.` : ret
}

/**
 * "$4,370 collected in Stripe from $3,120 of Meta spend, Sep 19 to Sep 25 · 36 trials started".
 * `otherDollars` is revenue matched to non-ad visits and `notMatchedDollars`
 * revenue matched to no visit at all. Both are named separately so neither is
 * read as ad return, and unmatched money is never called a source.
 */
export function adsMeta(
  platform: string | null,
  provider: string,
  earned: number,
  spend: number,
  range: string | null,
  trials: number,
  money: Money,
  otherDollars = 0,
  notMatchedDollars = 0,
): string {
  const spent = `${money(spend)} of ${platform ? `${platform} ` : 'ad '}spend`
  const head = earned > 0 ? `${money(earned)} collected in ${provider} from ${spent}` : spent
  return [
    range ? `${head}, ${range}` : head,
    trials > 0 ? `${plural(trials, 'trial')} started` : null,
    otherDollars > 0 ? `${money(otherDollars)} ${earned > 0 ? 'more ' : ''}collected from other sources` : null,
    notMatchedDollars > 0 ? `${money(notMatchedDollars)} not matched to a visit` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** The next wider preset for "Show last 30 days"; null when already wide. */
export function widerPeriod(period: string | undefined): { period: string; label: string } | null {
  const short = ['1d', 'PAST_24_H', 'TODAY', 'YESTERDAY', '7d', 'LAST_7_DAYS', 'WEEK_TO_DATE']
  if (!period || short.includes(period) || /[:_]/.test(period)) return { period: '30d', label: 'Show last 30 days' }
  if (['30d', 'LAST_30_DAYS', 'mtd', 'MONTH_TO_DATE'].includes(period)) return { period: '90d', label: 'Show last 90 days' }
  if (['90d', 'LAST_90_DAYS', 'QUARTER_TO_DATE'].includes(period)) return { period: '12m', label: 'Show last 12 months' }
  return null
}
